import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { releaseReadinessApi } from '../services/api';

// Query key shared by the report query and the publish mutation's cache update.
const reportKey = ({ fixVersion, releaseBranch, projectKey = 'ADM', sprintId = null }) => [
  'releaseReadiness',
  fixVersion,
  releaseBranch,
  projectKey,
  sprintId,
];

// Fetch the release readiness report for a Jira fix version + release branch.
// Only runs when both fixVersion and releaseBranch are provided. Passing a sprintId
// (optional) additionally folds that sprint's stories into the report.
export const useReleaseReadiness = (fixVersion, releaseBranch, projectKey = 'ADM', sprintId = null) => {
  return useQuery({
    queryKey: reportKey({ fixVersion, releaseBranch, projectKey, sprintId }),
    queryFn: () => releaseReadinessApi.getReport({ fixVersion, releaseBranch, projectKey, sprintId }),
    enabled: !!fixVersion && !!releaseBranch,
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// Publish the current report's snapshot to the release's Confluence page. Deliberately a mutation
// with no automatic refetch: the response *is* the freshly-built report (the server re-runs it as
// part of publishing), so it's written straight into the query cache. Invalidating instead would
// throw away the publish outcome and pay for a second full Jira + GitHub run.
export const usePublishReleaseReadiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params) => releaseReadinessApi.publish(params),
    onSuccess: (response, params) => {
      // The axios response interceptor already unwraps to the API envelope, so `response.data`
      // is the report itself and the cached value has the same { success, data } shape.
      const report = response?.data;
      if (!report) return;
      queryClient.setQueryData(reportKey(params), (old) => (old ? { ...old, data: report } : old));
    },
  });
};

// Fetch the sprints available on a project's board, used to populate the optional
// sprint-inclusion picker. Only runs when explicitly enabled (e.g. the toggle is on).
export const useReleaseReadinessSprints = (projectKey = 'ADM', enabled = false) => {
  return useQuery({
    queryKey: ['releaseReadinessSprints', projectKey],
    queryFn: () => releaseReadinessApi.getSprints(projectKey),
    enabled: enabled && !!projectKey,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
